import {
  SHOP_CART_ACCESSORY_AMOUNT_SET,
  SHOP_CART_ACCESSORY_REMOVE,
  SHOP_CART_ADDRESS_FIELD_SET,
  SHOP_CART_ADDRESS_SET,
  SHOP_CART_COMMENT_SET,
  SHOP_CART_FIELD_SET,
  SHOP_CART_NOF_CONTROLLERS_SET,
  SHOP_CART_NOF_SUBSCRIPTIONS_SET,
  SHOP_CART_ORDER_FAIL,
  SHOP_CART_ORDER_FETCH,
  SHOP_CART_ORDER_SET,
  SHOP_CART_ORDER_STOP,
  SHOP_CART_PLATE_PICKUP_SET,
  SHOP_CART_RESET,
  SHOP_CART_SCALE_AMOUNT_SET,
  SHOP_CART_SCALE_REMOVE,
  SHOP_CART_SUBSCRIPTION_YEARS_SET,
  SHOP_CART_VALIDATE_FAIL,
  SHOP_CART_VALIDATE_FETCH,
  SHOP_CART_VALIDATE_SET,
  SHOP_FETCH_CONFIG,
  SHOP_ORDERS_FETCH,
  SHOP_ORDERS_SET,
  SHOP_ORDER_COMPLETED,
  SHOP_SET_CONFIG,
  SHOP_SET_CONFIG_ERROR,
  SHOP_SET_CONFIG_YAML,
  SHOP_TOTAL_SET,
  cartReset,
} from "./shopActions.js";

export const CURRENT_CART_SCHEMA = 6;
export const MODE_DIGITAL = "digital";
export const MODE_HARDWARE = "hardware+digital";

export type ValidatedItem = {
  description: string;
  amount: number;
  totalPrice: string;
};

type CheckoutState = {
  fetching: boolean;
  total: any;
  paymentUrl: any;
  paymentMethods: any[];
};

type ValidateState = {
  fetching: boolean;
  total: string;
  items: any[];
};

type Currency = string; // "chf" and what else?
export type CartState = {
  schema: number;
  nofSubscriptions: number;
  mode: "digital" | "hardware+digital";
  subscriptionYears: number;
  currency: Currency;
  controllers: object;
  scales: object;
  accessories: object;
  address: {
    email: string;
    firstname: string;
    lastname: string;
    street: string;
    zip: string;
    city: string;
    country: string;
    phone: string;
    company: string;
  };
  subscriptionControllerId: number | null;
  platePickup: any;
  comment: string;
  checkout: CheckoutState;
  validate: ValidateState;
};

export type LastOrder = {
  orderkey: string;
  paymentMethod: "internal" | "wallee" | null;
  items: ValidatedItem[];
  total: string;
  address: CartState["address"];
  mode: string;
};

const defaultCheckoutState: CheckoutState = {
  fetching: false,
  total: null,
  paymentUrl: null,
  paymentMethods: [],
};

const defaultValidateState: ValidateState = {
  fetching: false,
  total: "",
  items: [],
};

const defaultCartState: CartState = {
  schema: CURRENT_CART_SCHEMA,
  nofSubscriptions: 0,
  mode: MODE_HARDWARE,
  subscriptionYears: 2,
  currency: "chf",
  subscriptionControllerId: null,
  controllers: {},
  scales: {},
  accessories: {},
  address: {
    email: "",
    firstname: "",
    lastname: "",
    street: "",
    zip: "",
    city: "",
    country: "ch",
    phone: "",
    company: "",
  },
  platePickup: null,
  comment: "",
  checkout: defaultCheckoutState,
  validate: defaultValidateState,
};

export const hardwareCartOverride = {
  nofControllers: 1,
};

export const digitalCartOverride = {
  mode: MODE_DIGITAL,
  nofSubscriptions: 1,
};

const defaultState = {
  config: {
    open: false,
    fetching: false,
    total: null,
  },
  cart: defaultCartState,
  edit: {
    config: null,
    error: null,
  },
  orders: {
    list: [],
    fetching: false,
  },
  lastOrder: null as LastOrder | null,
};

type CartAction = any; // | {type: typeof SHOP_CART_FIELD_SET, field: keyof CartState, value: any}

export const cart = (state = defaultCartState, action: CartAction) => {
  switch (action.type) {
    case SHOP_CART_RESET:
      return {
        ...defaultCartState,
        ...action.overrides,
        address: {
          ...state.address,
        },
      };
    case SHOP_CART_SUBSCRIPTION_YEARS_SET:
      return {
        ...state,
        subscriptionYears: action.years,
      };
    case SHOP_CART_PLATE_PICKUP_SET:
      return {
        ...state,
        platePickup: action.provider,
      };
    case SHOP_CART_COMMENT_SET:
      return {
        ...state,
        comment: action.comment,
      };
    case SHOP_CART_NOF_SUBSCRIPTIONS_SET:
      return {
        ...state,
        nofSubscriptions: action.amount,
      };
    case SHOP_CART_NOF_CONTROLLERS_SET:
      return {
        ...state,
        nofControllers: action.amount,
        controllers: {
          ...state.controllers,
          [action.key]: Math.max(0, action.amount),
        },
      };
    case SHOP_CART_ADDRESS_SET:
      return {
        ...state,
        address: action.address,
      };
    case SHOP_CART_ADDRESS_FIELD_SET:
      return {
        ...state,
        address: {
          ...state.address,
          [action.field]: action.value,
        },
      };
    case SHOP_CART_FIELD_SET:
      return {
        ...state,
        [action.field]: action.value,
      };
    case SHOP_CART_ACCESSORY_AMOUNT_SET:
      return {
        ...state,
        accessories: {
          ...state.accessories,
          [action.accessory]: Math.max(0, action.amount),
        },
      };
    case SHOP_CART_ACCESSORY_REMOVE:
      //TODO
      return state;
    case SHOP_CART_SCALE_AMOUNT_SET:
      return {
        ...state,
        scales: {
          ...state.scales,
          [action.scale]: Math.max(0, action.amount),
        },
      };
    case SHOP_CART_SCALE_REMOVE: {
      const remainingScales = { ...(state.scales as Record<string, unknown>) };
      delete remainingScales[action.scale];
      return { ...state, scales: remainingScales };
    }

    case SHOP_CART_ORDER_FETCH:
      return {
        ...state,
        checkout: {
          ...defaultCheckoutState,
          fetching: true,
        },
      };
    case SHOP_CART_ORDER_STOP:
      return {
        ...state,
        checkout: {
          ...state.checkout,
          fetching: false,
        },
      };
    case SHOP_CART_ORDER_SET:
      return {
        ...state,
        checkout: {
          ...action,
          type: undefined,
          fetching: false,
        },
      };
    case SHOP_CART_ORDER_FAIL:
      return {
        ...state,
        checkout: {
          ...state.checkout,
          fetching: false,
        },
      };
    case SHOP_CART_VALIDATE_FETCH:
      return { ...state, validate: { ...state.validate, fetching: true } };
    case SHOP_CART_VALIDATE_SET:
      return {
        ...state,
        validate: {
          ...state.validate,
          total: action.value.total,
          items: action.value.items,
          fetching: false,
        },
      };
    case SHOP_CART_VALIDATE_FAIL:
      return { ...state, validate: { ...state.validate, fetching: false } };
    default:
      return state;
  }
};

export type ShopState = Omit<ReturnType<typeof shop>, "cart"> & {
  cart: CartState;
};
export const shop = (state = defaultState, action: any) => {
  if (action.type.startsWith("SHOP_CART")) {
    return {
      ...state,
      cart: cart(state.cart, action),
    };
  }
  switch (action.type) {
    case SHOP_FETCH_CONFIG:
      return {
        ...state,
        config: {
          ...state.config,
          fetching: true,
        },
      };
    case SHOP_SET_CONFIG:
      return {
        ...state,
        config: {
          ...action.config,
          fetching: false,
        },
      };
    case SHOP_SET_CONFIG_YAML:
      return {
        ...state,
        edit: {
          ...state.edit,
          config: action.yamlConfig,
        },
      };
    case SHOP_SET_CONFIG_ERROR:
      return {
        ...state,
        edit: {
          ...state.edit,
          error: action.error,
        },
      };
    case SHOP_TOTAL_SET:
      return {
        ...state,
        fetching: false,
        total: action.total,
      };
    case SHOP_ORDERS_FETCH:
      return {
        ...state,
        orders: {
          ...state.orders,
          fetching: true,
        },
      };
    case SHOP_ORDERS_SET:
      return {
        ...state,
        orders: {
          ...state.orders,
          list: action.orders,
          fetching: false,
        },
      };
    case SHOP_ORDER_COMPLETED: {
      if (state.lastOrder?.orderkey === action.orderkey) {
        return state;
      }
      return {
        ...state,
        lastOrder: {
          orderkey: action.orderkey,
          paymentMethod: action.paymentMethod,
          items: state.cart.validate.items,
          total: state.cart.validate.total,
          address: state.cart.address,
          mode: state.cart.mode,
        },
        cart: cart(state.cart, cartReset(MODE_HARDWARE)),
      };
    }
    default:
      return state;
  }
};
